import { useQuery } from '@tanstack/react-query';
import type { FranchiseStage, FranchiseStatusDTO } from '@bifeai/shared';
import { Badge, Card, CardBody, CardHeader } from '@bifeai/ui';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';

const STAGE: Record<FranchiseStage, { label: string; tone: 'neutral' | 'warning' | 'success' }> = {
  NOT_STARTED: { label: 'Sin empezar', tone: 'neutral' },
  IN_PROGRESS: { label: 'En curso', tone: 'warning' },
  READY: { label: 'Listo', tone: 'success' },
};

/**
 * Para la casa central: cómo van los trámites de cada franquicia (Mercado Pago y facturación).
 * Se ve el avance, nunca los datos: la franquicia es otra empresa y los hace el franquiciado.
 */
export function FranchiseStatusCard() {
  const { isHeadquartersUser, me } = useSession();
  const hasFranchises = me.branches.some((b) => b.franchise);
  const q = useQuery({
    queryKey: ['franchises', 'status'],
    queryFn: () => api.get<FranchiseStatusDTO[]>('/branches/franchises/status'),
    enabled: isHeadquartersUser && hasFranchises,
  });
  if (!isHeadquartersUser || !hasFranchises || !q.data?.length) return null;
  return (
    <Card>
      <CardHeader
        title="Trámites de las franquicias"
        subtitle="Cada franquicia es otra empresa: conecta su Mercado Pago y hace su trámite de facturación desde su usuario. Acá ves cómo van, sin sus datos."
      />
      <CardBody flush>
        <div className="bf-table-wrap">
          <table className="bf-table">
            <thead>
              <tr>
                <th>Franquicia</th>
                <th>Mercado Pago</th>
                {q.data.some((f) => f.arca) && <th>Facturación ARCA</th>}
              </tr>
            </thead>
            <tbody>
              {q.data.map((f) => (
                <tr key={f.branchId}>
                  <td>
                    <span className="bf-cell-main">{f.name}</span>
                    {f.users === 0 && <div className="bf-cell-sub">Sin usuario: creale uno en Usuarios para que haga sus trámites.</div>}
                  </td>
                  <td>
                    <Badge tone={STAGE[f.mercadoPago.stage].tone}>{STAGE[f.mercadoPago.stage].label}</Badge>
                    <div className="bf-cell-sub">{f.mercadoPago.detail}</div>
                  </td>
                  {f.arca && (
                    <td>
                      <Badge tone={STAGE[f.arca.stage].tone}>{STAGE[f.arca.stage].label}</Badge>
                      <div className="bf-cell-sub">{f.arca.detail}</div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
